<script lang="ts" setup>
import * as _ from 'lodash-es'
import { getRdbmsApiUrl } from '@/lib/rdbms-api'
import { isDisplayableImage } from '@/lib/remote-data/normalize'
import {
  DateWithPrecision,
  normalizeDateString,
  parsePrecision,
} from '@/lib/remote-data/date-with-precision'
import { formatDate, formatDates, formatList, matomo } from '@/lib/helpers'

const route = useRoute()
const history = useRouterHistory()

const personId = computed(() => String(route.params.id ?? ''))

const filterInputStore = useFilterInputStore()
const optionsStore = useOptionsStore()
const lifecycleStore = useLifecycleStore()

const endpoint = computed(() =>
  personId.value === '' ? '' : getRdbmsApiUrl(`person/${encodeURIComponent(personId.value)}`),
)

const {
  isFetching,
  error,
  data: rawProfile,
} = useFetch(endpoint, {
  refetch: true,
  immediate: true,
}).get().json<any>()

// Keep debug slots in the template intact.
const baseQuery = computed(() => '')
const relationsQuery = computed(() => '')
const positionQuery = computed(() => '')
const occupationQuery = computed(() => '')
const partyAffiliationQuery = computed(() => '')
const placesQuery = computed(() => '')
const rawDataArray = computed(() => (rawProfile.value ? [rawProfile.value] : []))
const simplifiedDataArray = computed(() => rawDataArray.value)

function parseDate({ value, precision }: { value: string; precision: string }) {
  const date = new DateWithPrecision(normalizeDateString(value))
  date.setPrecision(parsePrecision(precision))
  return date
}

function parseDateValue(value: unknown): Date | null {
  if (typeof value !== 'string' || value === '') return null
  const normalized = normalizeDateString(value)
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

type RelationEntry = { value: string; label: string }
type NamedEntry = { id: string; name: string }
type DatedEntry = NamedEntry & { date: [Date | null, Date | null] }
type PositionEntry = {
  uid: string
  defaultLabel: string
  customLabel?: string
  label: string
  value: string
  date: [Date | null, Date | null]
}
type PersonViewModel = {
  id: string
  label: string
  gender?: string
  datesOfBirth: Date[]
  datesOfDeath: Date[]
  fathers: RelationEntry[]
  mothers: RelationEntry[]
  siblings: RelationEntry[]
  spouses: RelationEntry[]
  children: RelationEntry[]
  placeOfBirth?: NamedEntry
  placeOfDeath?: NamedEntry
  placesOfOrigin: NamedEntry[]
  placesOfActivity: DatedEntry[]
  placesOfResidence: DatedEntry[]
  partyAffiliations: DatedEntry[]
  positions: PositionEntry[]
  occupations: NamedEntry[]
  gnd?: string
  wikipediaUrl?: string
  images: string[]
}

const person = computed<PersonViewModel | undefined>(() => {
  const item = rawProfile.value
  if (!item || typeof item !== 'object') return undefined

  const positions = (item.positions ?? [])
    .flatMap((position: any) => {
      if (!position || typeof position !== 'object') return []
      const defaultLabel = String(position.label ?? '')
      if (defaultLabel === '') return []

      let customLabel: string | undefined
      for (const definedPosition of optionsStore.positions) {
        for (const detail of definedPosition.details) {
          if (detail.entity_id !== position.value) continue

          if (detail.qualifier_id && detail.qualifier_value) {
            const qualifiers = Array.isArray(position.qualifiers) ? position.qualifiers : []
            const hasQualifier = qualifiers.some(
              (entry: any) =>
                entry?.id === detail.qualifier_id && entry?.value === detail.qualifier_value,
            )
            if (!hasQualifier) continue
          }

          customLabel = definedPosition.label
          break
        }

        if (customLabel) break
      }

      const dateRange = Array.isArray(position.date) ? position.date : [null, null]

      return [
        {
          uid: crypto.randomUUID(),
          defaultLabel,
          customLabel,
          label: customLabel ?? defaultLabel,
          value: String(position.value ?? ''),
          date: [parseDateValue(dateRange[0]), parseDateValue(dateRange[1])] as [Date | null, Date | null],
        },
      ]
    })
    .sort((a: PositionEntry, b: PositionEntry) => (a.date[0]?.getTime() ?? 0) - (b.date[0]?.getTime() ?? 0))

  const mapDatedPlaces = (value: unknown): DatedEntry[] =>
    (Array.isArray(value) ? value : [])
      .flatMap((entry: any) => {
        if (!entry || typeof entry !== 'object') return []
        const date = Array.isArray(entry.date) ? entry.date : [null, null]
        return [
          {
            id: String(entry.id ?? ''),
            name: String(entry.name ?? ''),
            date: [parseDateValue(date[0]), parseDateValue(date[1])] as [Date | null, Date | null],
          },
        ]
      })
      .filter((entry: DatedEntry) => entry.id !== '' && entry.name !== '')
      .sort((a: DatedEntry, b: DatedEntry) => (a.date[0]?.getTime() ?? 0) - (b.date[0]?.getTime() ?? 0))

  const mapDatedTerms = (value: unknown): DatedEntry[] =>
    (Array.isArray(value) ? value : [])
      .flatMap((entry: any) => {
        if (!entry || typeof entry !== 'object') return []
        const date = Array.isArray(entry.date) ? entry.date : [null, null]
        return [
          {
            id: String(entry.id ?? ''),
            name: String(entry.name ?? ''),
            date: [parseDateValue(date[0]), parseDateValue(date[1])] as [Date | null, Date | null],
          },
        ]
      })
      .filter((entry: DatedEntry) => entry.id !== '' && entry.name !== '')
      .sort((a: DatedEntry, b: DatedEntry) => (a.date[0]?.getTime() ?? 0) - (b.date[0]?.getTime() ?? 0))

  const datesOfBirth = (Array.isArray(item.datesOfBirth) ? item.datesOfBirth : [])
    .flatMap((entry: any) =>
      typeof entry?.value === 'string' && typeof entry?.precision === 'string'
        ? [parseDate({ value: entry.value, precision: entry.precision })]
        : [],
    )
    .filter((entry: Date) => !Number.isNaN(entry.getTime()))

  const datesOfDeath = (Array.isArray(item.datesOfDeath) ? item.datesOfDeath : [])
    .flatMap((entry: any) =>
      typeof entry?.value === 'string' && typeof entry?.precision === 'string'
        ? [parseDate({ value: entry.value, precision: entry.precision })]
        : [],
    )
    .filter((entry: Date) => !Number.isNaN(entry.getTime()))

  return {
    id: String(item.id ?? ''),
    label: String(item.label ?? '?'),
    gender: typeof item.gender === 'string' ? item.gender : undefined,
    datesOfBirth,
    datesOfDeath,
    fathers: Array.isArray(item.fathers) ? item.fathers : [],
    mothers: Array.isArray(item.mothers) ? item.mothers : [],
    siblings: Array.isArray(item.siblings) ? item.siblings : [],
    spouses: Array.isArray(item.spouses) ? item.spouses : [],
    children: Array.isArray(item.children) ? item.children : [],
    placeOfBirth:
      item.placeOfBirth && typeof item.placeOfBirth === 'object'
        ? {
            id: String(item.placeOfBirth.id ?? ''),
            name: String(item.placeOfBirth.name ?? ''),
          }
        : undefined,
    placeOfDeath:
      item.placeOfDeath && typeof item.placeOfDeath === 'object'
        ? {
            id: String(item.placeOfDeath.id ?? ''),
            name: String(item.placeOfDeath.name ?? ''),
          }
        : undefined,
    placesOfOrigin: (Array.isArray(item.placesOfOrigin) ? item.placesOfOrigin : [])
      .flatMap((entry: any) =>
        entry?.id && entry?.name ? [{ id: String(entry.id), name: String(entry.name) }] : [],
      )
      .filter((entry: NamedEntry) => entry.id !== '' && entry.name !== ''),
    placesOfActivity: mapDatedPlaces(item.placesOfActivity),
    placesOfResidence: mapDatedPlaces(item.placesOfResidence),
    partyAffiliations: mapDatedTerms(item.partyAffiliations),
    positions,
    occupations: (Array.isArray(item.occupations) ? item.occupations : [])
      .flatMap((entry: any) =>
        entry?.id && entry?.name ? [{ id: String(entry.id), name: String(entry.name) }] : [],
      )
      .filter((entry: NamedEntry) => entry.id !== '' && entry.name !== ''),
    gnd: typeof item.gnd === 'string' ? item.gnd : undefined,
    wikipediaUrl: typeof item.wikipediaUrl === 'string' ? item.wikipediaUrl : undefined,
    images: (Array.isArray(item.images) ? item.images : []).filter(isDisplayableImage),
  }
})

const { data: imageMetaData } = useImageMeta(
  () => person.value?.images ?? [],
)
const galleryImages = computed(() =>
  (person.value?.images ?? []).map((image: string) => ({
    source: image,
    alt: '',
    caption: imageMetaData.value.has(image) ? imageMetaData.value.get(image)?.artist : undefined,
  })),
)

const { isFetching: isFetchingMetagrid, resources: metagridResources } = useMetagrid(
  () => person.value?.gnd,
)
const groupedMetagridResources = computed(() => _.groupBy(metagridResources.value, 'category'))

const personLabel = computed(() => person.value?.label)

const feedbackUrl = computed(() => {
  const url = new URL(location.href)
  url.pathname = optionsStore.feedbackPath
  url.searchParams.set('person', personLabel.value ?? '')
  url.searchParams.set('link', location.href)

  return url.toString()
})

const head = computed(() => ({
  title: personLabel.value ? `${personLabel.value} | ${optionsStore.siteTitle}` : undefined,
  meta: [
    {
      name: 'robots',
      content: 'noindex, nofollow',
    },
  ],
}))
useHead(head)

watch(
  () => head.value.title,
  title => {
    if (!title) return
    matomo('setDocumentTitle', title)
  },
)

const mainRef = ref<HTMLElement>()
watch(
  mainRef,
  main => {
    if (!main) return

    nextTick(() => {
      const scrollOffset = main.getBoundingClientRect().top

      const threshold = 150

      if (scrollOffset < threshold) {
        window.scrollTo(0, window.scrollY - scrollOffset - threshold)
      }
    })
  },
  { immediate: true },
)

const { isFetching: fetchingArchiveBeacons, links: archiveBeacons } = useBeacons(
  () => person.value?.gnd,
  'archive',
)
const { isFetching: fetchingLibraryBeacons, links: libraryBeacons } = useBeacons(
  () => person.value?.gnd,
  'library',
)
const { isFetching: fetchingEncyclopediaBeacons, links: encyclopediaBeacons } = useBeacons(
  () => person.value?.gnd,
  'encyclopedia',
)
const { isFetching: fetchingMuseumBeacons, links: museumBeacons } = useBeacons(
  () => person.value?.gnd,
  'museum',
)
const { isFetching: fetchingDatabaseBeacons, links: databaseBeacons } = useBeacons(
  () => person.value?.gnd,
  'database',
)
const { isFetching: fetchingAuthorityFilesBeacons, links: authorityFilesBeacons } = useBeacons(
  () => person.value?.gnd,
  'authorityFiles',
)

const archiveLinks = computed(() => [
  ...archiveBeacons.value,
  ...(groupedMetagridResources.value.archive ?? []),
])
const libraryLinks = computed(() => [
  ...libraryBeacons.value,
  ...(groupedMetagridResources.value.library ?? []),
])
const encyclopediaLinks = computed(() => [
  ...encyclopediaBeacons.value,
  ...(groupedMetagridResources.value.encyclopedia ?? []),
  ...(person.value?.wikipediaUrl
    ? [
        {
          url: person.value.wikipediaUrl,
          title: 'Wikipedia',
        },
      ]
    : []),
])
const museumLinks = computed(() => [
  ...museumBeacons.value,
  ...(groupedMetagridResources.value.museum ?? []),
])
const databaseLinks = computed(() => [
  ...databaseBeacons.value,
  ...(groupedMetagridResources.value.database ?? []),
])
const authorityFilesLinks = computed(() => [
  ...authorityFilesBeacons.value,
  ...(groupedMetagridResources.value.authority_files ?? []),
])

onMounted(() => {
  lifecycleStore.initialized = true
})
</script>

<template>
  <!-- Debug Information -->
  <div v-if="false">
    <details>
      <summary>Queries</summary>
      Base Query:
      <pre style="white-space: pre-wrap">{{ baseQuery }}</pre>
      <br />

      Relations Query:
      <pre style="white-space: pre-wrap">{{ relationsQuery }}</pre>
      <br />

      Position Query:
      <pre style="white-space: pre-wrap">{{ positionQuery }}</pre>
      <br />

      Occupation Query:
      <pre style="white-space: pre-wrap">{{ occupationQuery }}</pre>
      <br />

      Party Affiliation Query:
      <pre style="white-space: pre-wrap">{{ partyAffiliationQuery }}</pre>
      <br />

      Places Query:
      <pre style="white-space: pre-wrap">{{ placesQuery }}</pre>
      <br />
      <hr />
    </details>
    <details>
      <summary>Raw Responses Data</summary>
      <pre style="white-space: pre-wrap">{{ rawDataArray }}</pre>
      <hr />
    </details>
    <details>
      <summary>Simplified Data</summary>
      <pre style="white-space: pre-wrap">{{ simplifiedDataArray }}</pre>
      <hr />
    </details>
    <details>
      <summary>Normalized Data</summary>
      <pre style="white-space: pre-wrap">{{ person }}</pre>
      <hr />
    </details>
    <details>
      <summary>Metagrid</summary>
      <pre style="white-space: pre-wrap">{{ metagridResources }}</pre>
      <hr />
    </details>
  </div>
  <div v-if="isFetching" style="width: fit-content; margin: auto">
    <ProgressSpinner style="width: 24px; height: 24px" strokeWidth="4" animationDuration=".5s" />
  </div>
  <template v-else-if="person">
    <header>
      <div class="name-and-more">
        <h2 class="h5 primary-line-background">
          {{ person.label }}
        </h2>
        <span>
          {{
            formatList(
              person.occupations.map(occupation => occupation.name),
              'unit',
            )
          }}
        </span>
      </div>
      <RouterLink :to="String(history.state.back ?? '/')" class="back-button">
        <button aria-label="Zurück">
          <svg
            width="32px"
            height="32px"
            viewBox="0 0 16.0466 16.0385"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <g id="Artboard-1" transform="translate(-709.1406, -99.5584)" stroke="#981000">
                <g id="close" transform="translate(709.1406, 99.5584)">
                  <line x1="0.0156" y1="0.0075" x2="16.0466" y2="16.0385" id="Stroke-29"></line>
                  <line x1="0" y1="16.031" x2="16.031" y2="0" id="Stroke-30"></line>
                </g>
              </g>
            </g>
          </svg>
        </button>
      </RouterLink>
    </header>

    <main ref="mainRef">
      <!-- Person Data -->
      <div class="person-details">
        <div class="details-table">
          <template v-if="person.datesOfBirth.length > 0">
            <div class="section-title">Geburtsdatum</div>
            <div class="section-content">
              {{ formatDates(person.datesOfBirth, 'long') }}
            </div>
          </template>
          <template v-if="person.placeOfBirth">
            <span class="section-title">Geburtsort</span>
            <div class="section-content">
              <FilterLink
                :params="{
                  placeOfBirth: [person.placeOfBirth],
                }"
              >
                {{ person.placeOfBirth.name }}
              </FilterLink>
            </div>
          </template>
          <template v-if="person.datesOfDeath.length > 0">
            <div class="section-title">Sterbedatum</div>
            <div class="section-content">
              {{ formatDates(person.datesOfDeath, 'long') }}
            </div>
          </template>
          <template v-if="person.placeOfDeath">
            <span class="section-title">Sterbeort</span>
            <div class="section-content">
              <FilterLink
                :params="{
                  placeOfDeath: [person.placeOfDeath],
                }"
              >
                {{ person.placeOfDeath.name }}
              </FilterLink>
            </div>
          </template>
          <template v-if="person.placesOfOrigin.length > 0">
            <span class="section-title">Bürgerort</span>
            <div class="section-content">
              <template v-for="(placeOfOrigin, index) in person.placesOfOrigin">
                <br v-if="index > 0" />
                <FilterLink
                  :params="{
                    placeOfOrigin: [placeOfOrigin],
                  }"
                >
                  {{ placeOfOrigin.name }}
                </FilterLink>
              </template>
            </div>
          </template>
          <template v-if="person.placesOfResidence.length > 0">
            <span class="section-title">Wohnort</span>
            <div class="section-content place-of-residence">
              <template v-for="(placeOfResidence, index) in person.placesOfResidence">
                <template v-if="index > 0"><br /></template>
                <FilterLink
                  :params="{
                    placeOfResidence: [placeOfResidence],
                  }"
                >
                  {{ placeOfResidence.name }}
                </FilterLink>

                <template v-if="placeOfResidence.date[0] || placeOfResidence.date[1]">
                  <template v-if="!placeOfResidence.date[1]">
                    (ab {{ formatDate(placeOfResidence.date[0]!, 'year') }})
                  </template>
                  <template v-else-if="!placeOfResidence.date[0]">
                    (bis {{ formatDate(placeOfResidence.date[1]!, 'year') }})
                  </template>
                  <template v-else>
                    ({{ formatDate(placeOfResidence.date[0]!, 'year') }} –
                    {{ formatDate(placeOfResidence.date[1]!, 'year') }})
                  </template>
                </template>
              </template>
            </div>
          </template>
          <template v-if="person.placesOfActivity.length > 0">
            <span class="section-title">Wirkungsort</span>
            <div class="section-content">
              <template v-for="(placeOfActivity, index) in person.placesOfActivity">
                <br v-if="index > 0" />
                <FilterLink
                  :params="{
                    placeOfActivity: [placeOfActivity],
                  }"
                >
                  {{ placeOfActivity.name }}
                </FilterLink>

                <template v-if="placeOfActivity.date[0] || placeOfActivity.date[1]">
                  <template v-if="!placeOfActivity.date[1]">
                    (ab {{ formatDate(placeOfActivity.date[0]!, 'year') }})
                  </template>
                  <template v-else-if="!placeOfActivity.date[0]">
                    (bis {{ formatDate(placeOfActivity.date[1]!, 'year') }})
                  </template>
                  <template v-else>
                    ({{ formatDate(placeOfActivity.date[0]!, 'year') }} –
                    {{ formatDate(placeOfActivity.date[1]!, 'year') }})
                  </template>
                </template>
              </template>
            </div>
          </template>
        </div>
        <div class="details-table">
          <template v-if="person.fathers.length">
            <span class="section-title">Vater</span>
            <div class="section-content">
              <template v-for="(father, index) in person.fathers">
                <template v-if="index > 0">,{{ ' ' }}</template>
                {{ father.label }}
              </template>
            </div>
          </template>
          <template v-if="person.mothers.length">
            <span class="section-title">Mutter</span>
            <div class="section-content">
              <template v-for="(mother, index) in person.mothers">
                <template v-if="index > 0">,{{ ' ' }}</template>
                {{ mother.label }}
              </template>
            </div>
          </template>
          <template v-if="person.siblings.length">
            <span class="section-title">Geschwister</span>
            <div class="section-content">
              <template v-for="(sibling, index) in person.siblings">
                <br v-if="index > 0" />
                {{ sibling.label }}
              </template>
            </div>
          </template>
          <template v-if="person.spouses.length">
            <span class="section-title">(Ehe-) Partner</span>
            <div class="section-content">
              <template v-for="(spouse, index) in person.spouses">
                <br v-if="index > 0" />
                {{ spouse.label }}
              </template>
            </div>
          </template>
          <template v-if="person.children.length">
            <span class="section-title">Kinder</span>
            <div class="section-content">
              <template v-for="(child, index) in person.children">
                <br v-if="index > 0" />
                {{ child.label }}
              </template>
            </div>
          </template>
          <FilterLink
            v-if="person.datesOfBirth.length > 0 && person.datesOfDeath.length > 0"
            :params="{
              dateOfBirthRange: [filterInputStore.minDate, person.datesOfDeath[0]],
              dateOfDeathRange: [person.datesOfBirth[0], filterInputStore.maxDate],
            }"
          >
            Zeitgenossen
          </FilterLink>
        </div>
      </div>

      <!-- Images -->
      <section class="images" v-if="person?.images.length > 0">
        <h3 class="h5 primary-line-background">Bilder</h3>
        <div class="section-content">
          <Gallery :images="galleryImages" />
        </div>
      </section>

      <!-- Positions -->
      <section class="positions" v-if="person.positions.length > 0">
        <h3 class="h5 primary-line-background">Ämter</h3>
        <div class="section-content">
          <div v-for="(item, index) in person.positions">
            <FilterLink
              v-if="item.customLabel"
              :params="{
                positions: [
                  {
                    uid: item.uid,
                    position: [
                      {
                        label: item.customLabel,
                        details: [],
                      },
                    ],
                    date: [null, null],
                  },
                ],
              }"
            >
              {{ item.label }}
            </FilterLink>
            <span v-else>
              {{ item.label }}
            </span>

            <template v-if="item.date[0] || item.date[1]">
              <template v-if="!item.date[1]">
                (ab {{ formatDate(item.date[0]!, 'year') }})
              </template>
              <template v-else-if="!item.date[0]">
                (bis {{ formatDate(item.date[1]!, 'year') }})
              </template>
              <template v-else>
                ({{ formatDate(item.date[0]!, 'year') }} – {{ formatDate(item.date[1]!, 'year') }})
              </template>
            </template>
          </div>
        </div>
      </section>

      <!-- Party Affiliations -->
      <section class="party-affiliations" v-if="person.partyAffiliations.length > 0">
        <h3 class="h5 primary-line-background">Parteizugehörigkeit</h3>
        <div class="section-content">
          <div v-for="(partyAffiliation, index) in person.partyAffiliations">
            <FilterLink
              v-if="optionsStore.parties.some(party => party.id === partyAffiliation.id)"
              :params="{
                partyAffiliation: [partyAffiliation],
              }"
            >
              {{ partyAffiliation.name }}
            </FilterLink>
            <span v-else>
              {{ partyAffiliation.name }}
            </span>

            <template v-if="partyAffiliation.date[0] || partyAffiliation.date[1]">
              <template v-if="!partyAffiliation.date[1]">
                (ab {{ formatDate(partyAffiliation.date[0]!, 'year') }})
              </template>
              <template v-else-if="!partyAffiliation.date[0]">
                (bis {{ formatDate(partyAffiliation.date[1]!, 'year') }})
              </template>
              <template v-else>
                ({{ formatDate(partyAffiliation.date[0]!, 'year') }} –
                {{ formatDate(partyAffiliation.date[1]!, 'year') }})
              </template>
            </template>
          </div>
        </div>
      </section>

      <!-- Further Links -->
      <div class="further-links">
        <h3 class="h5 primary-line-background">Weiterführende Links</h3>
        <div class="layout">
          <section>
            <div class="section-title">Archive</div>
            <div class="section-content">
              <ProgressSpinner
                v-if="fetchingArchiveBeacons"
                style="width: 24px; height: 24px"
                strokeWidth="4"
                animationDuration=".5s"
              />
              <List v-else-if="archiveLinks.length > 0" :items="archiveLinks" v-slot="{ item }">
                <ExternalLink :url="item.url">
                  {{ item.title }}
                </ExternalLink>
                <WebsiteOnlyInfo v-if="'website_only' in item && item.website_only" />
              </List>
              <template v-else>—</template>
            </div>
          </section>
          <section>
            <div class="section-title">Bibliotheken</div>
            <div class="section-content">
              <ProgressSpinner
                v-if="fetchingLibraryBeacons || isFetchingMetagrid"
                style="width: 24px; height: 24px"
                strokeWidth="4"
                animationDuration=".5s"
              />
              <List v-else-if="libraryLinks.length > 0" :items="libraryLinks" v-slot="{ item }">
                <ExternalLink :url="item.url">
                  {{ item.title }}
                </ExternalLink>
                <WebsiteOnlyInfo v-if="'website_only' in item && item.website_only" />
              </List>
              <template v-else>—</template>
            </div>
          </section>
          <section>
            <div class="section-title">Lexika</div>
            <div class="section-content">
              <ProgressSpinner
                v-if="fetchingEncyclopediaBeacons"
                style="width: 24px; height: 24px"
                strokeWidth="4"
                animationDuration=".5s"
              />
              <List
                v-else-if="encyclopediaLinks.length > 0"
                :items="encyclopediaLinks"
                v-slot="{ item }"
              >
                <ExternalLink :url="item.url">
                  {{ item.title }}
                </ExternalLink>
                <WebsiteOnlyInfo v-if="'website_only' in item && item.website_only" />
              </List>
              <template v-else>—</template>
            </div>
          </section>
          <section>
            <div class="section-title">Museen und Sammlungen</div>
            <div class="section-content">
              <ProgressSpinner
                v-if="fetchingMuseumBeacons"
                style="width: 24px; height: 24px"
                strokeWidth="4"
                animationDuration=".5s"
              />
              <List v-else-if="museumLinks.length > 0" :items="museumLinks" v-slot="{ item }">
                <ExternalLink :url="item.url">
                  {{ item.title }}
                </ExternalLink>
                <WebsiteOnlyInfo v-if="'website_only' in item && item.website_only" />
              </List>
              <template v-else>—</template>
            </div>
          </section>
          <section>
            <div class="section-title">Normdaten</div>
            <div class="section-content">
              <div v-if="person.gnd">
                <ExternalLink :url="`https://d-nb.info/gnd/${person.gnd}`">
                  Gemeinsame Normdatei GND
                </ExternalLink>
                &nbsp;
                <span class="gray-text">{{ person.gnd }}</span>
              </div>
              <div>
                <ExternalLink :url="`https://www.wikidata.org/wiki/${personId}`">
                  Wikidata
                </ExternalLink>
                &nbsp;
                <span class="gray-text">{{ personId }}</span>
              </div>
              <div v-if="fetchingAuthorityFilesBeacons || authorityFilesLinks.length > 0">
                <ProgressSpinner
                  v-if="fetchingAuthorityFilesBeacons"
                  style="width: 24px; height: 24px"
                  strokeWidth="4"
                  animationDuration=".5s"
                />
                <List v-else :items="authorityFilesLinks" v-slot="{ item }">
                  <ExternalLink :url="item.url">{{ item.title }}</ExternalLink>
                </List>
              </div>
            </div>
          </section>
          <section>
            <div class="section-title">Datenbanken</div>
            <div class="section-content">
              <ProgressSpinner
                v-if="fetchingDatabaseBeacons"
                style="width: 24px; height: 24px"
                strokeWidth="4"
                animationDuration=".5s"
              />
              <List v-else-if="databaseLinks.length > 0" :items="databaseLinks" v-slot="{ item }">
                <ExternalLink :url="item.url">
                  {{ item.title }}
                </ExternalLink>
                <WebsiteOnlyInfo v-if="'website_only' in item && item.website_only" />
              </List>
              <template v-else>—</template>
            </div>
          </section>
        </div>
      </div>

      <div class="section-wide feedback-section">
        <a :href="feedbackUrl">Rückmeldung geben</a>
      </div>
    </main>
  </template>
  <template v-else>
    <Message severity="error" :closable="false">
      Über diese Person konnten keine Informationen abgerufen werden.
    </Message>
  </template>
</template>

<style lang="scss" scoped>
@use '@/styles/util' as *;

header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 1em;
  align-items: center;
  padding: 0;
  margin-bottom: 2em;
  background-color: #fff;
  //top: calc(var(--scroll-offset, 0px) - 2px);
  //position: sticky;
  //z-index: 1;

  > *,
  > * > * {
    margin: 0;
  }

  .name-and-more {
    display: flex;
    gap: 0.5ch 2ch;
    flex-direction: column;
    justify-content: flex-start;
    width: fit-content;

    @media (min-width: 700px) {
      flex-direction: row;
      align-items: center;
    }

    .gender {
      font-weight: 600;
    }
  }

  .back-button {
    display: inline-flex;
    align-self: start;

    @media (min-width: 700px) {
      align-self: center;
    }

    button {
      appearance: none;
      border: none;
      background-color: transparent;
      padding: 0;
      display: inline-flex;
    }
  }
}

main {
  container: person / inline-size;
  display: grid;
  grid-gap: 4.5em;
}

h3 {
  display: inline-flex;
  margin: 0 0 2em 0;
}

.person-details {
  display: grid;
  grid-gap: 2em;
  grid-template-columns: 1fr;
  align-items: flex-start;

  @container (width >= 700px) {
    grid-template-columns: 1fr 1fr;
  }

  .details-table {
    display: grid;
    grid-gap: 0.5em 1.5em;
    grid-template-columns: auto 1fr;

    & > :nth-child(odd) {
      font-weight: bold;
    }
  }
}

.place-of-residence {
  max-width: 30rem;
}

.images {
}

.positions {
}

.further-links {
  .layout {
    display: grid;
    grid-gap: 3em;
    grid-template-columns: 1fr;

    @container (width >= 700px) {
      grid-template-columns: 1fr 1fr;
    }

    section {
      border-top: 1px solid var(--primary-color);

      .section-title {
        font-weight: bold;
        margin: 0.5em 0;
      }
    }
  }
}

.feedback-section {
  margin-top: 2.5rem;
  display: block;
}

footer {
  display: flex;
  background-color: #fff;
  padding: 1em 0;
  position: sticky;
  bottom: 0;
  justify-content: space-between;
}

.metagrid {
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
}

.gray-text {
  color: #bfc0bf;
}
</style>
